import { hkdf } from "@noble/hashes/hkdf.js";
import { sha256 } from "@noble/hashes/sha2.js";
import { concatBytes, utf8ToBytes } from "@noble/hashes/utils.js";
import { SecureChannel } from "./channel";
import { dh, generateKeyPair, type KeyPair } from "./keys";

/**
 * Handshake modelled on Noise_IK (see docs/adr/0002-crypto.md).
 *
 * The initiator (phone) already knows the responder's (daemon's) static public key
 * from pairing. Messages:
 *   msg1 (initiator -> responder): initiator ephemeral pub (32) || initiator static pub (32)
 *   msg2 (responder -> initiator): responder ephemeral pub (32)
 *
 * Session keys come from HKDF over four DH results (es, ss, ee, se), salted with the
 * transcript. Only the holders of both static private keys can derive them, so there
 * is no separate signature step: the first frame that decrypts proves identity.
 */

const KEY_LEN = 32;
const MSG1_LEN = KEY_LEN * 2;
const MSG2_LEN = KEY_LEN;
const INFO = utf8ToBytes("foreman/v1/session-keys");

export interface InitiatorHandshake {
  /** Send this to the responder. */
  message1: Uint8Array;
  /** Feed the responder's reply to get the established channel. */
  finish(message2: Uint8Array): SecureChannel;
}

export function startHandshake(local: KeyPair, remoteStaticPublicKey: Uint8Array): InitiatorHandshake {
  const eph = generateKeyPair();
  const message1 = concatBytes(eph.publicKey, local.publicKey);
  return {
    message1,
    finish(message2) {
      if (message2.length !== MSG2_LEN) throw new Error("bad handshake message 2");
      const remoteEphemeral = message2;
      const keys = deriveKeys({
        es: dh(eph.secretKey, remoteStaticPublicKey),
        ss: dh(local.secretKey, remoteStaticPublicKey),
        ee: dh(eph.secretKey, remoteEphemeral),
        se: dh(local.secretKey, remoteEphemeral),
        transcript: transcript(message1, message2, remoteStaticPublicKey),
      });
      return new SecureChannel(keys.initiatorToResponder, keys.responderToInitiator);
    },
  };
}

export interface ResponderResult {
  /** Send this back to the initiator. */
  message2: Uint8Array;
  channel: SecureChannel;
  /** The initiator's static public key, already checked against `isKnownPeer`. */
  peerPublicKey: Uint8Array;
}

/**
 * `isKnownPeer` is the device-authorization check: only keys the user paired
 * (and has not revoked) may complete a handshake.
 */
export function respondToHandshake(
  local: KeyPair,
  message1: Uint8Array,
  isKnownPeer: (peerPublicKey: Uint8Array) => boolean,
): ResponderResult {
  if (message1.length !== MSG1_LEN) throw new Error("bad handshake message 1");
  const remoteEphemeral = message1.subarray(0, KEY_LEN);
  const peerPublicKey = message1.slice(KEY_LEN, MSG1_LEN);
  if (!isKnownPeer(peerPublicKey)) throw new Error("unknown or revoked device");

  const eph = generateKeyPair();
  const message2 = eph.publicKey;
  const keys = deriveKeys({
    es: dh(local.secretKey, remoteEphemeral),
    ss: dh(local.secretKey, peerPublicKey),
    ee: dh(eph.secretKey, remoteEphemeral),
    se: dh(eph.secretKey, peerPublicKey),
    transcript: transcript(message1, message2, local.publicKey),
  });
  return {
    message2,
    peerPublicKey,
    channel: new SecureChannel(keys.responderToInitiator, keys.initiatorToResponder),
  };
}

function transcript(msg1: Uint8Array, msg2: Uint8Array, responderStatic: Uint8Array): Uint8Array {
  return sha256(concatBytes(msg1, msg2, responderStatic));
}

function deriveKeys(p: {
  es: Uint8Array;
  ss: Uint8Array;
  ee: Uint8Array;
  se: Uint8Array;
  transcript: Uint8Array;
}) {
  const okm = hkdf(sha256, concatBytes(p.es, p.ss, p.ee, p.se), p.transcript, INFO, KEY_LEN * 2);
  return {
    initiatorToResponder: okm.slice(0, KEY_LEN),
    responderToInitiator: okm.slice(KEY_LEN),
  };
}

